import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import Table from '@/components/ui/tables/Table';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { DateRangeFilter } from '@/components/ui/filters/DateRangeFilter';
import { IndicatorCard } from '@/components/ui/cards/IndicatorCard';
import { useAbsenceList } from '@/hooks/absence/useAbsenceList';
import { formatDateToYYYYMMDD, getMinDateByMonths } from '@/utils';


export const AbsenceSummary: React.FC = () => {
  const {
    filters,
    setFilters,
    defaultFrom,
    defaultTo,
    filterDefinitions,
    handleFilterChange,
    data,
    totals,
    totalRecords,
    isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    columns,
    actions,
    exportAbsences,
    isExporting,
    translate,
  } = useAbsenceList();

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="absence-summary-page">
        {/* Indicator Cards */}
        <div className="flex flex-row gap-4 overflow-x-auto pb-4 px-2">
          <IndicatorCard
            label={translate('indicators.licencias')}
            value={totals.licencias}
            variant="default"
            data-testid="indicator-licencias"
          />
          <IndicatorCard
            label={translate('indicators.vacaciones')}
            value={totals.vacaciones}
            variant="default"
            data-testid="indicator-vacaciones"
          />
          <IndicatorCard
            label={translate('indicators.permisosEspeciales')}
            value={totals.permisosEspeciales}
            variant="default"
            data-testid="indicator-permisos-especiales"
          />
          <IndicatorCard
            label={translate('indicators.permisosAdministrativos')}
            value={totals.permisosAdministrativos}
            variant="default"
            data-testid="indicator-permisos-administrativos"
          />
          <IndicatorCard
            label={translate('indicators.permisosFraccion')}
            value={totals.permisosFraccion}
            variant="default"
            data-testid="indicator-permisos-fraccion"
          />
          <IndicatorCard
            label={translate('indicators.faltas')}
            value={totals.faltas}
            variant="default"
            data-testid="indicator-faltas"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex flex-wrap gap-3 items-center">
            <DateRangeFilter
              value={{ from: filters.from, to: filters.to }}
              defaultValue={{ from: defaultFrom, to: defaultTo }}
              onChange={(range) => {
                setFilters((prev) => ({ ...prev, from: range.from, to: range.to }));
              }}
              maxDate={formatDateToYYYYMMDD(new Date())}
              minDate={getMinDateByMonths()}
              closeOnComplete
            />
            <FilterDropdown
              filters={filterDefinitions}
              values={{
                tipoAusentismo: filters.tipoAusentismo,
                estado: filters.estado,
              }}
              onChangeFilter={handleFilterChange}
            />
          </div>
          <ExportDropdown
            onExport={exportAbsences}
            placement="right"
            isLoading={isExporting}
            availableFormats={['EXCEL', 'PDF', 'WORD']}
          />
        </div>

        {/* Table */}
        <Table
          columns={columns}
          data={data}
          totalRows={totalRecords}
          isLoading={isLoading}
          searchable={true}
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          pagination={{
            page,
            pageSize,
            total: totalRecords,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          initialSort={{ key: 'fechaCreacion', direction: 'desc' }}
          actions={actions}
          data-testid="table-ausentismos"
        />
      </div>
    </PageLayout>
  );
};

export default AbsenceSummary;
